import { PortalContainer, portalStoreToken, usePortalModeOptions } from '@servicetitan/cp-ui';
import {
    ActionMenu,
    Avatar,
    BodyText,
    Button,
    Headline,
    Keys,
    Link,
    Stack,
} from '@servicetitan/design-system';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { tokens } from '@servicetitan/tokens/core';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { FC, ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PortalAuthStore } from '../../../auth/stores/portal-auth.store';
import { termsLink } from '../../../auth/utils/terms';
import { useAuthHistory } from '../../../auth/utils/use-auth-history';
import { AppSettingsStore } from '../../stores/app-settings.store';
import { NavigationStore } from '../../stores/navigation.store';
import { WebPortalStore } from '../../stores/portal.store';
import { SchedulingWrapper } from '../scheduler/scheduling-wrapper';
import { PortalLayoutMenu } from './portal-layout-menu';
import * as Styles from './portal-layout.module.less';

interface PortalLayoutProps {
    children?: ReactNode;
}

export const PortalLayoutProvider = provide({ singletons: [NavigationStore] })(
    ({ children }) => children
);

export const PortalLayoutComponent: FC<PortalLayoutProps> = observer(({ children }) => {
    const [{ authorized, authorizedLoginless, logout }, { directLinksSignUpSignInEnabled }] =
        useDependencies(PortalAuthStore, AppSettingsStore);
    const [{ setPortalMode, settings, user }] = useDependencies<[WebPortalStore]>(
        portalStoreToken as any
    );
    const authHistory = useAuthHistory();
    const location = useLocation();

    const [userContextOpen, setUserContextOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    const mode = usePortalModeOptions();

    useEffect(() => {
        setPortalMode(mode);
    }, [mode, setPortalMode]);

    const onSignOut = () => {
        setUserContextOpen(false);
        setMenuOpen(false);
        logout();
    };

    const closeMenu = () => {
        setMenuOpen(false);
    };

    const toggleUserMenu = () => {
        setUserContextOpen(!userContextOpen);
    };

    const handleAvatarKeyPress = (e: KeyboardEvent) => {
        if ([Keys.Enter, Keys.Space].includes(e.code as Keys)) {
            toggleUserMenu();
        }
    };

    const getLoginPath = () => {
        const path = location.pathname + location.search;
        const loginPath = authHistory.path(
            'login',
            undefined,
            path && path !== '/' ? { redirect: encodeURI(path) } : undefined
        );

        if (!settings?.host) {
            return loginPath;
        }
        return `${window.location.protocol}//${settings.host}${loginPath}`;
    };

    return (
        <PortalContainer mode={mode} className={classNames('min-h-100vh')}>
            <div
                className={Styles.header}
                data-tracking-section="portal-layout-header"
                data-cy="portal-layout-header"
            >
                {!authorizedLoginless && !mode.sidebarMenu && (
                    <Button
                        className={Styles.hamburger}
                        iconName="menu"
                        fill="subtle"
                        onClick={() => setMenuOpen(true)}
                        aria-label="Menu"
                    />
                )}

                <img src={settings?.logo} className={Styles.logo} alt="" />

                <Stack className={Styles.headerContent}>
                    <Headline size="medium" className={classNames('m-b-0', Styles.headerTitle)}>
                        {settings?.title}
                    </Headline>
                </Stack>

                {!authorized && directLinksSignUpSignInEnabled && settings?.host && (
                    <Button fill="outline" size="small" href={getLoginPath()}>
                        Sign In
                    </Button>
                )}

                {authorized && settings?.bookingEnabled && <SchedulingWrapper />}

                {authorized && !mode.mobile && (
                    <ActionMenu
                        open={userContextOpen}
                        onClickOutside={
                            userContextOpen ? () => setUserContextOpen(false) : undefined
                        }
                        trigger={
                            <Avatar
                                name={user?.name}
                                color={tokens.colorBlueGrey600}
                                className="m-l-2 cursor-pointer"
                                onClick={toggleUserMenu}
                                onKeyPress={handleAvatarKeyPress}
                                aria-label="User"
                                tabIndex="0"
                                data-testid="user-button"
                            />
                        }
                    >
                        <BodyText className={classNames('p-l-3 p-x-1', Styles.userName)}>
                            {user?.name}
                        </BodyText>
                        <ActionMenu.Group>
                            <ActionMenu.Item
                                onClick={onSignOut}
                                data-tracking-id="signout-button"
                                className="m-b-1"
                            >
                                <BodyText bold className="c-neutral-200">
                                    Sign out
                                </BodyText>
                            </ActionMenu.Item>
                        </ActionMenu.Group>
                        <Stack
                            className={Styles.termsContainer}
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Link href={termsLink} target="_blank">
                                Terms of Service
                            </Link>
                        </Stack>
                    </ActionMenu>
                )}
            </div>

            <div className={Styles.contentWrapper}>
                <PortalLayoutMenu
                    authorized={authorized}
                    menuOpen={menuOpen}
                    mode={mode}
                    onClose={closeMenu}
                    onSignOut={onSignOut}
                />

                <div
                    className={Styles.contentWrapperInner}
                    data-tracking-section="portal-layout-content"
                    data-cy="portal-layout-content"
                >
                    {children}
                </div>
            </div>
        </PortalContainer>
    );
});

export const PortalLayout: FC<PortalLayoutProps> = props => (
    <PortalLayoutProvider>
        <PortalLayoutComponent {...props} />
    </PortalLayoutProvider>
);
